import { useState, useEffect, useCallback } from 'react';
import {
  fetchTokens,
  createApiToken,
  revokeApiToken,
  type TokenInfo,
  type CreateTokenResult,
} from '../api/client';

interface SetupScreenProps {
  onSetupComplete: (token: string) => void;
}

export function SetupScreen({ onSetupComplete }: SetupScreenProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdToken, setCreatedToken] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await createApiToken({ name, email, role: 'user' });
      setCreatedToken(result.token);
    } catch (err: any) {
      setError(err.message || 'Failed to create token');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-md p-8 w-full max-w-md">
        <h1 className="text-xl font-semibold text-gray-800 mb-1">
          Welcome to Shelve Override Configuration
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          Create your first admin token to get started.
        </p>

        {createdToken ? (
          <div>
            <div className="bg-green-50 border border-green-200 rounded p-4 mb-4">
              <p className="text-sm font-medium text-green-800 mb-2">Token created successfully!</p>
              <p className="text-xs text-green-700 mb-2">
                Copy this token now. It will only be shown once.
              </p>
              <div className="flex gap-2">
                <code className="flex-1 bg-white border border-green-300 rounded px-2 py-1.5 text-xs font-mono break-all select-all">
                  {createdToken}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(createdToken)}
                  className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Copy
                </button>
              </div>
            </div>
            <button
              onClick={() => onSetupComplete(createdToken)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded"
            >
              Continue to App
            </button>
          </div>
        ) : (
          <form onSubmit={handleCreate}>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Admin"
                required
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

            <button
              type="submit"
              disabled={loading || !name.trim() || !email.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Admin Token'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export function TokenManagement() {
  const [tokens, setTokens] = useState<TokenInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newToken, setNewToken] = useState<CreateTokenResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchTokens();
      setTokens(data);
    } catch (err) {
      console.error('Failed to load tokens:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevoke = async (id: number, name: string) => {
    if (!confirm(`Revoke token "${name}"? This cannot be undone.`)) return;
    try {
      await revokeApiToken(id);
      load();
    } catch (err) {
      console.error('Failed to revoke token:', err);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-base font-medium text-gray-700">API Tokens</h2>
        <button
          onClick={() => { setShowCreate(true); setNewToken(null); }}
          className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded"
        >
          + New Token
        </button>
      </div>

      {/* Token table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700">ID</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Name</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Email</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Role</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Created</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Last Used</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-gray-400">Loading...</td>
              </tr>
            ) : tokens.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-gray-400">No tokens</td>
              </tr>
            ) : (
              tokens.map((t) => (
                <tr key={t.id} className="border-t border-gray-100 hover:bg-blue-50">
                  <td className="px-3 py-1.5">{t.id}</td>
                  <td className="px-3 py-1.5">{t.name}</td>
                  <td className="px-3 py-1.5">{t.email}</td>
                  <td className="px-3 py-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      t.role === 'user' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {t.role}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-gray-500">
                    {formatDate(t.createdAt)}
                  </td>
                  <td className="px-3 py-1.5 text-gray-500">
                    {t.lastUsedAt ? formatDate(t.lastUsedAt) : 'Never'}
                  </td>
                  <td className="px-3 py-1.5">
                    {t.revoked ? (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        Revoked
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    {!t.revoked && (
                      <button
                        onClick={() => handleRevoke(t.id, t.name)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create token modal */}
      {showCreate && (
        <CreateTokenModal
          result={newToken}
          onCreate={(result) => { setNewToken(result); load(); }}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}

function CreateTokenModal({
  result,
  onCreate,
  onClose,
}: {
  result: CreateTokenResult | null;
  onCreate: (result: CreateTokenResult) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'user' | 'service'>('user');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await createApiToken({ name, email, role });
      onCreate(res);
    } catch (err: any) {
      setError(err.message || 'Failed to create token');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {result ? 'Token Created' : 'Create New Token'}
        </h3>

        {result ? (
          <div>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
              <p className="text-xs font-medium text-amber-800 mb-2">
                Copy this token now. It will only be shown once.
              </p>
              <div className="flex gap-2">
                <code className="flex-1 bg-white border border-amber-300 rounded px-2 py-1.5 text-xs font-mono break-all select-all">
                  {result.token}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(result.token)}
                  className="px-3 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700"
                >
                  Copy
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              <strong>{result.name}</strong> ({result.email}) &mdash; {result.role}
            </p>
            <button
              onClick={onClose}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm py-2 rounded"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleCreate}>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John's token"
                required
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
                required
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as 'user' | 'service')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="user">User (full access)</option>
                <option value="service">Service (read-only)</option>
              </select>
            </div>

            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm py-2 rounded"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !name.trim() || !email.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 rounded disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Token'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
